<template>
  <b-form-group
    :class="inputClass"
    :label="labelIsHtml ? null : inputLabel"
    :label-for="inputId"
    class="mb-0"
    :invalidFeedback="invalidFeedback"
    :state="state">
    <template v-if="labelIsHtml" v-slot:label>
      <span v-html="inputLabel"></span>
    </template>
    <div>
      <b-form-input
        :id="inputId"
        v-model="inputValue"
        :disabled="disabled"
        :placeholder="placeholder"
        :required="required"
        :tabindex="tabIndex"
        autocomplete="off"
        type="text"
        @input="onChange();"
        @keydown.down="onArrowDown"
        @keydown.up="onArrowUp"
        @keydown.enter="onEnter"
        @click="onClick()"
      />
      <ul v-show="isOpen" id="autocomplete-elements" class="autocomplete-elements" :class="{'autocomplete-elements-hidden': elements.length === 0 && !(inputValue && needCreateIfEmpty && inputValue.trim() !== '')}">
        <li v-for="(element, i) in elements" :key="i" :class="{ 'is-active': i === arrowCounter }" class="autocomplete-element" @click="setElement(element)">
          <span v-html="element.formattedText"></span>
        </li>
        <li v-if="needCreateIfEmpty && inputValue && elements.length === 0 && inputValue.trim() !== ''"
            class="autocomplete-element create-new"
            @click="createNewItem">
          {{$t('Create')}} "{{ inputValue }}"
        </li>
      </ul>
    </div>
  </b-form-group>
</template>

<script>

export default {
  name: 'AutocompleteField',

  props: {
    items: {
      type: Array,
      required: false,
      default: () => []
    },
    initialValue: {
      required: false,
      default: null
    },
    readyToSetInitialValue: {
      type: Boolean,
      required: false,
      default: false
    },
    inputId: {
      type: String,
      required: true
    },
    inputLabel: {
      type: String,
      required: true
    },
    labelIsHtml: {
      type: Boolean,
      required: false,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    inputClass: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    tabIndex: {
      type: Number,
      required: false
    },
    invalidFeedback: {
      default: null
    },
    state: {
      default: null
    },
    needCreateIfEmpty:{
      type : Boolean,
      default:false
    }
  },

  data () {
    return {
      isOpen: false,
      initialValueFromView: null,
      elements: [...this.items],
      arrowCounter: -1,
      searchInput: { value: null, text: null},
      inputValue: ''
    }
  },

  watch: {

    inputValue (newValue) {
      this.filterElements()
    },

    items (newVal) {
      this.elements = [...newVal]
      this.reset()
      this.initValue()
    },

    /** doit être placée dessous items () !!! pour que l'init se fasse après la dernière modification de items due au chargement de la page d'origine */
    readyToSetInitialValue (newValue) {
      if(newValue) {
        this.initValue()
      }
    },

    'initialValue': {
      immediate:true,
      handler () {
        this.initialValueFromView = this.initialValue
        this.initValue()
      }
    },
  },

  mounted () {
    document.addEventListener('click', this.handleClickOutside)
    this.$emit('autocompleteMounted')
    this.initialValueFromView = this.initialValue
  },

  destroyed () {
    document.removeEventListener('click', this.handleClickOutside)
  },

  emits: ['autocompleteMounted', 'newSelectedValue', 'valueModified','createNewItem'],

  methods: {

    initValue () {
      if(this.initialValueFromView) {
        var itemFound = null
        this.items.forEach((item) => {
          if(item.value === this.initialValueFromView) {
            itemFound = item
          }
        })

        if(itemFound) {
          this.setElement(itemFound)
        }
      }
    },

    setElement (element) {
      this.searchInput = { ...element }
      this.inputValue = element.text
      this.isOpen = false
      this.emitNewSelectedValue(element.value)
    },

    filterElements () {
      var searchLower = this.inputValue.toLowerCase()
      this.elements = this.items
        .filter((item) =>
          item.text.toLowerCase().includes(searchLower)
        )
        .map((item) => {
          let formattedText = item.text
          if (item.text.toLowerCase().includes(searchLower)) {
            const matchStart = item.text.toLowerCase().indexOf(searchLower)
            const matchEnd = matchStart + searchLower.length
            const beforeMatch = item.text.slice(0, matchStart)
            const matchText = item.text.slice(matchStart, matchEnd)
            const afterMatch = item.text.slice(matchEnd)
            formattedText = `<span>${beforeMatch}</span><span class="autocomplete-match">${matchText}</span><span>${afterMatch}</span>`
          }
          return { ...item, formattedText }
        })
        .sort((itemA, itemB) => {
          if(searchLower === '') {
            return 0
          }
          var indexA = itemA.text.toLowerCase().indexOf(searchLower)
          var indexB = itemB.text.toLowerCase().indexOf(searchLower)

          if ((indexA == -1 && indexB != -1) || (indexA != -1 && indexB != -1 && indexB < indexA)) {
            return 1 //B est devant A
          } else if ((indexB == -1 && indexA != -1) || (indexB != -1 && indexA != -1 && indexA < indexB)) {
            return -1 //A est devant B
          }
          //A = B
          return 0
        })
    },

    onClick () {
      this.filterElements()
      this.isOpen = true
    },

    onChange () {
      this.$emit('valueModified')
      this.filterElements()
      this.isOpen = true
      if(this.searchInput !== null) {
        this.searchInput = null
        this.emitNewSelectedValue(null)
      }
    },

    handleClickOutside (event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false
        this.arrowCounter = -1
      }
    },

    onArrowDown () {
      if (this.arrowCounter < this.elements.length) {
        this.arrowCounter = this.arrowCounter + 1
      }
    },
    onArrowUp () {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter - 1
      }
    },
    onEnter (evt) {
      if (this.arrowCounter !== -1) {
        evt.preventDefault()
        this.setElement(this.elements[this.arrowCounter])
        this.arrowCounter = -1
      }
    },

    emitNewSelectedValue (value) {
      this.$emit('newSelectedValue', value)
    },

    reset () {
      this.inputValue = ''
      this.searchInput = null
      this.emitNewSelectedValue(null)
    },

    createNewItem() {
      // Emit an event to create a new item with the current input value
      this.$emit('createNewItem', this.inputValue);
      // Close the autocomplete suggestions
      this.isOpen = false;
      // Reset the input value and selected item
      this.inputValue = '';
      this.searchInput = null;
    },
  }
}

</script>

<!-- not scoped car html insered --><style>
.autocomplete-match {
  color: var(--main-yellow-darker);
  font-weight: bold;
}
</style>

<style scoped>
.autocomplete {
  position: relative;
}

.autocomplete-elements {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  background-color: var(--main-black-dark);
  color: var(--main-white);
  position: absolute;
  z-index: 10;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
}
.autocomplete-elements-hidden {
  display: none;
}

.autocomplete-element {
  list-style: none;
  text-align: left;
  padding: 4px 10px;
  cursor: pointer;
}

.autocomplete-element.is-active,
.autocomplete-element:hover {
  background-color: var(--main-yellow-medium);
  color: var(--main-black-dark);
}

</style>



// WEBPACK FOOTER //
// src/components/b122/editFields/AutocompleteField.vue